import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Button, Surface } from '@americanexpress/dls-react';
import {
  IconChevronLeft,
} from '@americanexpress/dls-icons';
import { useMediaBreakpoint } from '@americanexpress/consumer-travel-base';
import { useRoomsContext } from '../../contexts/roomsContext';
import AddRoomButton from '../AddRoomButton/AddRoomButton';
import TooltipFooter from './TooltipFooter';
import TooltipBody from './TooltipBody';
import { useEscape } from '../../hooks/useEscape';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useConfigContext } from '../../contexts/configContext';
import styles from './Tooltip.scss';
import RoomVariant from '../Room/RoomVariant';

const RoomsTooltipVariant = memo(({ tooltipTriggerRef, tooltipPopoverRef }) => {
  const {
    tooltipVisible, rooms, setTooltipVisible, setRoomsToDefaultValues,
  } = useRoomsContext();
  const { hasRooms, maxRooms, moduleId } = useConfigContext();

  const { formatMessage } = useIntl();

  const refocusOnTrigger = useCallback(() => {
    tooltipTriggerRef.current.focus();
  }, [tooltipTriggerRef]);

  const closeRoomsDropdownAndRefocus = useCallback(() => {
    /* istanbul ignore next - this line is covered by Playwright at
    /__tests__/playwright/tooltip.spec.ts */
    if (tooltipVisible) {
      setTooltipVisible(false);
      refocusOnTrigger();
    }
  }, [refocusOnTrigger, setTooltipVisible, tooltipVisible]);

  useEscape(closeRoomsDropdownAndRefocus, []);

  useOnClickOutside(
    [tooltipPopoverRef, tooltipTriggerRef],
    closeRoomsDropdownAndRefocus
  );
  const isXSViewport = useMediaBreakpoint('xs-down');
  const isMobile = useMediaBreakpoint('sm-down');
  if (!tooltipVisible) {
    return null;
  }

  const isMultiRoom = hasRooms && maxRooms > 1 && rooms.length <= maxRooms;
  return (
    <Surface
      className={clsx(styles.tooltipVariant, 'flex flex-column shadow-3', isXSViewport && 'anim-slide-from-right')}
      id="Tooltip window"
      variant={isMobile ? 'base' : 'foreground'}
      ref={tooltipPopoverRef}
    >
      <Surface variant={isMobile ? 'foreground' : 'base'}>
        <div className={clsx('flex flex-row hidden-md-up border-b flex-justify-start')} style={{ padding: '0.25rem' }}>
          <Button
            icon={<IconChevronLeft size="md" />}
            variant="tertiary"
            onClick={closeRoomsDropdownAndRefocus}
          >
            {formatMessage({ id: 'back' })}
          </Button>
        </div>
      </Surface>
      <TooltipBody>
        {rooms.map((_, roomIndex) => (
          <RoomVariant
            canRemove={rooms.length > 1}
            isMultiRoom={isMultiRoom}
            key={`room-${roomIndex + 1}`}
            roomIndex={roomIndex}
          />
        ))}
        <AddRoomButton />
      </TooltipBody>
      <TooltipFooter>
        <div>
          <Button
            id={`${moduleId}_resetButton`}
            onClick={setRoomsToDefaultValues}
            variant="tertiary"
            className="margin-1-r"
          >
            {formatMessage({ id: 'reset' })}
          </Button>
          <Button
            id={`${moduleId}_doneButton`}
            onClick={closeRoomsDropdownAndRefocus}
            variant="primary"
          >
            {formatMessage({ id: 'done' })}
          </Button>
        </div>
      </TooltipFooter>
    </Surface>
  );
});

RoomsTooltipVariant.displayName = 'RoomsTooltip';

RoomsTooltipVariant.propTypes = {
  tooltipTriggerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.element, PropTypes.object]),
  tooltipPopoverRef: PropTypes.oneOfType([PropTypes.func, PropTypes.element, PropTypes.object]),
};

export { RoomsTooltipVariant as TestableRoomsTooltipVariant };

export default RoomsTooltipVariant;
